import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Package, FileText, MailCheck,
  Settings as SettingsIcon, History, LogOut, Inbox, CreditCard,
} from "lucide-react";
import { toast } from "sonner";

const items = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/invoices", label: "Invoices", icon: FileText },
  { to: "/admin/payments", label: "Quick Payments", icon: CreditCard },
  { to: "/admin/customers", label: "Customers", icon: Users },
  { to: "/admin/services", label: "Services", icon: Package },
  { to: "/admin/subscribers", label: "Subscribers", icon: MailCheck },
  { to: "/admin/contact", label: "Contact Inbox", icon: Inbox },
  { to: "/admin/audit", label: "Audit Log", icon: History },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon },
];

export default function AdminLayout({ children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const handleLogout = async () => {
    await logout();
    toast.success("Signed out");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0E13] text-[color:var(--ivory)]">
      <aside
        className="fixed inset-y-0 left-0 w-60 border-r border-white/5 bg-[#0A0D11] hidden lg:flex flex-col z-30"
        data-testid="admin-sidebar"
      >
        <div className="px-5 py-6 border-b border-white/5">
          <div className="font-display text-lg leading-tight">BDTT</div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[color:var(--ivory-muted)] mt-1">
            Admin Console
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                    isActive
                      ? "bg-[color:var(--bd-green)]/15 text-white border border-[color:var(--bd-green)]/30"
                      : "text-[color:var(--ivory-muted)] hover:bg-white/[0.04] hover:text-white"
                  }`
                }
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <Icon className="w-4 h-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
        <div className="p-3 border-t border-white/5">
          <div className="px-3 py-2 text-xs">
            <div className="text-[color:var(--ivory-muted)]">Signed in</div>
            <div className="truncate text-white">{user?.email}</div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-[color:var(--ivory-muted)] hover:bg-white/[0.04] hover:text-white transition"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="lg:pl-60">
        <header className="sticky top-0 z-20 bg-[#0B0E13]/80 backdrop-blur-md border-b border-white/5">
          <div className="px-5 sm:px-8 py-4 flex items-center justify-between">
            <div>
              <h1 className="font-display text-xl sm:text-2xl">{title}</h1>
            </div>
            <div className="text-xs text-[color:var(--ivory-muted)] hidden sm:block">
              {user?.email}
            </div>
          </div>
        </header>
        <main className="p-5 sm:p-8" data-testid="admin-main">
          {children}
        </main>
      </div>
    </div>
  );
}
